import { resetActiveFilters } from '@frontend/repository';
import { useEffect, useRef } from 'react';

import { useAppDispatch } from '../redux/store';

interface UseTabFiltersOptions {
    pageId: string;
    tabs: string[];
    activeTab: string;
}

/**
 * Custom hook for managing tab-based list isolation with automatic Redux filter clearing.
 *
 * This hook provides:
 * 1. Automatic Redux filter clearing when switching tabs
 * 2. Consistent list ID generation for tab-based state isolation
 *
 * Note: Tab state and query param management is handled by the TabFilter component.
 *
 * @param options Configuration object
 * @param options.pageId Unique identifier for the page (e.g., 'locations', 'users')
 * @param options.tabs Array of tab keys (e.g., ['spots', 'modules', 'slots'])
 * @param options.activeTab Current active tab (from TabFilter)
 *
 * @returns Object with getListId function for generating consistent list IDs
 *
 * @example
 * const viewProps = useTabFilter(props); // From TabFilter
 * const { getListId } = useTabFilters({
 *   pageId: 'locations',
 *   tabs: ['spots', 'modules', 'slots'],
 *   activeTab: viewProps.active
 * });
 *
 * // In component:
 * <ReactiveObjectList id={getListId(viewProps.active)} ... />
 */
export const useTabFilters = (options: UseTabFiltersOptions) => {
    const { pageId, tabs, activeTab } = options;
    const dispatch = useAppDispatch();
    const previousTabRef = useRef<string>(activeTab);

    // Generate composite list ID for a given tab
    const getListId = (tabKey: string): string => {
        return `${pageId}-${tabKey}-list`;
    };

    useEffect(() => {
        if (previousTabRef.current !== activeTab) {
            tabs.forEach((tabKey) => {
                const listId = getListId(tabKey);
                dispatch(resetActiveFilters({ listId }));
            });

            previousTabRef.current = activeTab;
        }
    }, [activeTab, dispatch, tabs, pageId]);

    return {
        getListId
    };
};
